import React, { useState, useEffect, useCallback } from 'react';
import { dataEntryApi } from '../../services/api';
import DynamicModule from '../../components/common/DynamicModule';

/**
 * DataEntryModulePage — runtime page for a Data Entry module.
 * Loads module config from AppCfg_DE_Modules and renders one tab per configured view.
 * Each view mounts DynamicModule locked to its view type with per-field entryMode overrides.
 *
 * Props:
 *   moduleSlug — matches Module_Slug in AppCfg_DE_Modules (e.g. "commissioning")
 */
const DataEntryModulePage = ({ moduleSlug }) => {
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeViewIndex, setActiveViewIndex] = useState(0);

  const loadModule = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await dataEntryApi.getModule(moduleSlug);
      setModule(res.data);
      setActiveViewIndex(0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load module');
    } finally {
      setLoading(false);
    }
  }, [moduleSlug]);

  useEffect(() => { loadModule(); }, [loadModule]);

  useEffect(() => {
    const handler = () => loadModule();
    window.addEventListener('pmd-database-changed', handler);
    return () => window.removeEventListener('pmd-database-changed', handler);
  }, [loadModule]);

  if (loading) return <div className="loading">Loading...</div>;

  if (error) {
    return (
      <div className="module-container">
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-red-500)' }}>
          {error}
        </div>
      </div>
    );
  }

  const views = module?.config?.views || [];

  if (views.length === 0) {
    return (
      <div className="module-container">
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-gray-400)' }}>
          <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{module?.module_Name || moduleSlug}</p>
          <p>No views configured. Use the Data Entry Composer to set up this module.</p>
        </div>
      </div>
    );
  }

  const sortedViews = [...views].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  const activeView = sortedViews[activeViewIndex] || sortedViews[0];

  // Build fieldOverrides from view's fieldConfig (entryMode per field)
  const buildFieldOverrides = (view) => {
    const overrides = {};
    (view.fieldConfig || []).forEach(fc => {
      if (fc.entryMode && fc.entryMode !== 'editable') {
        overrides[fc.fieldName] = { entryMode: fc.entryMode };
      }
    });
    return overrides;
  };

  return (
    <div className="module-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {sortedViews.length > 1 && (
        <div className="de-view-tabs">
          {sortedViews.map((view, idx) => (
            <button
              key={view.viewId || idx}
              className={`de-view-tab${idx === activeViewIndex ? ' active' : ''}`}
              onClick={() => setActiveViewIndex(idx)}
            >
              {view.viewLabel || view.tableName}
              <span className="de-view-type-badge">
                {view.viewType === 'datasheet' ? 'DS' : 'LV'}
              </span>
            </button>
          ))}
        </div>
      )}

      <div style={{ flex: 1, overflow: 'hidden' }}>
        <DynamicModule
          key={`${moduleSlug}-${activeViewIndex}-${activeView.tableName}`}
          tableName={activeView.tableName}
          forceViewType={activeView.viewType}
          fieldOverrides={buildFieldOverrides(activeView)}
        />
      </div>
    </div>
  );
};

export default DataEntryModulePage;
